/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Checkbox, Grid, Input } from '@scuf/common';
import React from 'react';
import { IThresholdValues } from './IAlarmConfiguration';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../locales/en-US/descriptors';

import '../../scss/AlarmConfigurationsVariables.scss';
import { validateWholeNumber } from '../shared/Utils/Utils';
import { PERCENTAGE_HUNDRED } from '../shared/Utils/Constants';

interface ThresholdSectionProps {
  severity: string;
  enabled: boolean;
  values: IThresholdValues;
  onChange(
    field: 'enabled' | 'AlarmName' | 'MinThresholdValue' | 'MaxThresholdValue',
    value: boolean | string
  ): void;
}

const ThresholdSection: React.FC<ThresholdSectionProps> = ({
  severity,
  enabled,
  values,
  onChange
}: ThresholdSectionProps) => {
  const d = descriptors.alarmConfiguration;
  const [t] = useTranslation();

  // Adding this function to escape any special character or html tags that might result in injection with Alarm Name
  const checkForSpecialCharacter = (text: string) =>
    text.replace(new RegExp(/[`@.!#$%^&*()+\-=\[\]{};':"\\|,<>\/?~]/, 'g'), '');

  const getSeverityImage = () => {
    if (severity.toUpperCase() === 'HIGH')
      return (
        <img
          className="priority-img"
          src={require('../../images/HighPriority.svg')}
          alt={severity}
        />
      );
    else if (severity.toUpperCase() === 'MEDIUM')
      return (
        <img
          className="priority-img"
          src={require('../../images/MediumPriority.svg')}
          alt={severity}
        />
      );
    else
      return (
        <img
          className="priority-img"
          src={require('../../images/LowPriority.svg')}
          alt={severity}
        />
      );
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={12} className="threshold-header">
          <>
            <Checkbox
              checked={enabled}
              onChange={(value) => onChange('enabled', value)}
            />
            {getSeverityImage()}
            <span>
              {severity} {t(d.THRESHOLDALARM)}
            </span>
          </>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={6}>
          <Input
            label={t(d.ALARMNAME)}
            disabled={!enabled}
            maxLength={PERCENTAGE_HUNDRED}
            fluid
            onChange={(text) =>
              onChange('AlarmName', checkForSpecialCharacter(text))
            }
            value={values.AlarmName}
          />
        </Grid.Column>
        <Grid.Column width={6}>
          {/* <Input label='Alarm Description' fluid /> */}
        </Grid.Column>
        <Grid.Column width={6}>
          <div className="threshold-min-max-section">
            <Input
              label={t(d.MINTHRESHOLD)}
              disabled={!enabled}
              icon={<span>%</span>}
              fluid
              iconPosition="right"
              maxLength={4}
              value={
                values.MinThresholdValue && values.MinThresholdValue.toString()
              }
              onChange={(text) =>
                onChange('MinThresholdValue', validateWholeNumber(text))
              }
            />
            <span
              className={
                severity !== 'HIGH' ? 'to' : 'to hide-threshold-section'
              }
            >
              {t(d.TO)}
            </span>
            <Input
              label={t(d.MAXTHRESHOLD)}
              disabled={!enabled}
              icon={<span>%</span>}
              fluid
              iconPosition="right"
              maxLength={4}
              value={
                values.MaxThresholdValue && values.MaxThresholdValue.toString()
              }
              className={severity === 'HIGH' ? 'hide-threshold-section' : ''}
              onChange={(text) =>
                onChange('MaxThresholdValue', validateWholeNumber(text))
              }
            />
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default ThresholdSection;
